import { IconProps } from 'Components/Icon/Icon';
import { TagProps } from 'Components/Tag/Tag';
import { ReportTableTab } from '../TrainingReports.types';

/** The state a row reports in its Status column. */
export enum RowStatus {
	Complete = 'complete',
	Incomplete = 'incomplete',
	Overdue = 'overdue',
	NotStarted = 'notStarted'
}

export interface RowStatusPresentation {
	label: string;
	tone: TagProps['type'];
}

/** A row on the Users tab. */
export interface UserRow {
	id: string;
	name: string;
	role: string;
	properties: string[];
	groups: string[];
	completion: number;
	pending: number;
	status: RowStatus;
}

/** A row on the Assignments tab. */
export interface AssignmentRow {
	id: string;
	name: string;
	courses: string[];
	courseType: string;
	properties: string[];
	groups: string[];
	assignedUsers: number;
	/** ISO date the whole assignment is due. */
	dueDate: string;
	completion: number;
	/** Not-started enrollments, out of `assignedUsers * courses.length`. */
	pending: number;
	overdue: number;
	status: RowStatus;
}

/** A row on the Courses tab. */
export interface CourseRow {
	id: string;
	name: string;
	category: string;
	/** "Required" or "Optional" — see `COURSE_TYPE`. */
	courseType: string;
	/** Properties the course reaches; the column shows the count. */
	properties: string[];
	groups: string[];
	/** Users the course is assigned to, shown under the course name. */
	enrolled: number;
	completion: number;
	pending: number;
	status: RowStatus;
}

/** One course line inside a user's details drawer. */
export interface UserCourseRow {
	id: string;
	name: string;
	category: string;
	status: RowStatus;
	/** ISO date; formatted for display by the drawer. */
	dueDate: string;
}

export interface UserPropertySection {
	id: string;
	property: string;
	/** The groups the user belongs to at this property. */
	groups: string[];
	courses: UserCourseRow[];
	completedCount: number;
}

/** Everything the details drawer shows for one user row. */
export interface UserDetail {
	totalCourses: number;
	completedCount: number;
	notStartedCount: number;
	overdueCount: number;
	sections: UserPropertySection[];
}

/** One participant's standing on a course, in the course details drawer. */
export interface CourseUserRow {
	id: string;
	name: string;
	role: string;
	status: RowStatus;
	dueDate: string | null;
}

/** One property's slice of a course, with the participants based there. */
export interface CoursePropertySection {
	id: string;
	property: string;
	groups: string[];
	completedCount: number;
	users: CourseUserRow[];
}

/** Everything the course details drawer shows for one row. */
export interface CourseDetail {
	enrolled: number;
	completedCount: number;
	notStartedCount: number;
	overdueCount: number;
	groupCount: number;
	certification: string;
	/** Names of the assignments this course is part of. */
	assignments: string[];
	sections: CoursePropertySection[];
}

/** One course inside an expanded assignment row. */
export interface AssignmentCourseRow {
	id: string;
	name: string;
	category: string;
	enrolled: number;
	completion: number;
	pending: number | null;
	status: RowStatus;
}

/** One participant's standing on one course, in the assignment drawer. */
export interface AssignmentUserRow {
	id: string;
	name: string;
	role: string;
	status: RowStatus;
}

/** One course's participant table within a property section. */
export interface AssignmentCourseSection {
	id: string;
	name: string;
	category: string;
	completedCount: number;
	users: AssignmentUserRow[];
}

/** One property's slice of an assignment, with a table per course. */
export interface AssignmentPropertySection {
	id: string;
	property: string;
	groups: string[];
	userCount: number;
	completedCount: number;
	totalCount: number;
	courses: AssignmentCourseSection[];
}

/** Everything the assignment details drawer shows for one row. */
export interface AssignmentDetail {
	totalUsers: number;
	courseCount: number;
	propertyCount: number | null;
	completion: number;
	notStartedCount: number;
	overdueCount: number;
	completedEnrollments: number;
	totalEnrollments: number;
	sections: AssignmentPropertySection[];
}

export interface ReportTableState<T> {
	rows: T[];
	summary: SummaryStat[];
	total: number;
	loading: boolean;
	page: number;
	pageSize: number;
	setPage: (page: number) => void;
	setPageSize: (pageSize: number) => void;
}

/** One figure on the bar between the filters and the table. */
export interface SummaryStat {
	key: string;
	label: string;
	value: number | string;
}

export interface ReportTabDefinition {
	key: ReportTableTab;
	label: string;
	icon: IconProps['type'];
	/** Placeholder the shared filter bar shows while this tab is active. */
	searchPlaceholder: string;
}
